/* @ngInject */

export default (
  $q,
  $rootScope,
  helperService,
  homeLoanService,
  Document,
  Note,
  Upload,
  OwnershipType,
  TransactionType,
  noteService,
  notesService,
  uiService,
  NOTE
) => {
  /**
   * View or save PDF.
   *
   * @param {Object} ticket
   * @param {Object} docDefinition
   * @param {String} fileName
   * @param {Boolean} isView
   *
   * @returns {Object}
   */
  const viewOrSavePdf = ({ ticket, docDefinition, fileName, isView }) => {
    const deferred = $q.defer();
    if (isView) {
      pdfMake.createPdf(docDefinition).open();
      deferred.resolve({ status: true });
    } else {
      uiService.toast({
        text: {
          p1: `Document ${fileName}`,
          p2: 'is being prepared',
          p3: 'please wait ...'
        },
        progress: true
      });
      pdfMake.createPdf(docDefinition).getDataUrl((dataURL) => {
        Upload.upload({
          url: NOTE.API.UPLOAD,
          data: {
            file: Upload.dataUrltoBlob(dataURL, fileName),
            idTicket: ticket.id
          }
        }).then((response) => {
          if (response?.status) {
            const newNote = Note.blank();
            newNote.idTicket = ticket.id;
            newNote.idWorkflow = ticket.idWorkflow;
            newNote.idOwner = $rootScope.CurrentAccount.id;
            newNote.type = 'automation';
            newNote.content = response.data.original;

            const attachment = Document.blank();
            attachment.idOwner = $rootScope.CurrentAccount.id;
            attachment.name = response.data.original;
            attachment.file = response.data.name;
            newNote.attachments.push(attachment);
            noteService.createNote(newNote).then((result) => {
              notesService.clearCache();
              if (result.status) {
                uiService.toast({
                  text: {
                    p1: `Document ${fileName}`,
                    p2: 'has been added',
                    p3: `to ${ticket.getName()}`
                  },
                  success: true
                });
              } else {
                uiService.toast({
                  text: {
                    p1: `Document ${fileName}`,
                    p2: 'has not been added',
                    p3: `to ${ticket.getName()}`
                  },
                  warn: true
                });
              }
              deferred.resolve({ status: true });
            });
          }
        });
      });
    }
    return deferred.promise;
  };

  /**
   * Create header.
   *
   * @param {String} title
   * @param {String} pageBreak
   *
   * @returns {Object}
   */
  const createHeader = ({ title, pageBreak }) => ({
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: title,
            style: 'header'
          }
        ]
      ]
    },
    layout: 'header',
    pageBreak: pageBreak || ''
  });

  /**
   * Create sub-header.
   *
   * @param {String} title
   * @param {Array} bodyStyle
   * @param {String} tableLayout
   *
   * @returns {Object}
   */
  const createSubHeader = ({ title, bodyStyle, tableLayout }) => ({
    table: {
      widths: ['*'],
      body: [
        [
          {
            text: title,
            style: bodyStyle || ['subHeader']
          }
        ]
      ]
    },
    style: bodyStyle ? [] : ['marginTop'],
    layout: tableLayout || 'subHeader'
  });

  /**
   * Prepare funds details by security for app form.
   *
   * @param {Array} homeLoanSecurities
   * @param {Object} fundRequired
   * @param {Object} fundAvailable
   *
   * @returns {Array}
   */
  const prepareFundsDetailsBySecurityForAppForm = ({
    homeLoanSecurities,
    fundRequired,
    fundAvailable
  }) => {
    const currentCurrency = $rootScope.CurrentCurrency();
    const data = [];
    if (
      fundRequired &&
      !_.isEmpty(fundRequired) &&
      fundAvailable &&
      !_.isEmpty(fundAvailable)
    ) {
      const security = _.find(homeLoanSecurities, (s) => s.id === fundRequired?.idSecurity);
      data.push(
        [
          {
            text: [
              {
                text: 'Security address\n',
                style: 'label'
              },
              {
                text: `${
                  security && security.address
                    ? `${security.address.state} ${security.address.postCode}`
                    : ``
                }`,
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Security value\n',
                style: 'label'
              },
              {
                text: (fundRequired?.purposeFunds || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Transaction type\n',
                style: 'label'
              },
              {
                text:
                  fundRequired.transactionType?.name !== TransactionType.none.name
                    ? fundRequired?.transactionType?.title || '-'
                    : '-',
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Ownership type\n',
                style: 'label'
              },
              {
                text:
                  fundRequired?.ownershipType?.name !== OwnershipType.none.name
                    ? fundRequired?.ownershipType?.title || '-'
                    : '-',
                style: 'value'
              }
            ]
          }
        ],
        [
          {
            text: [
              {
                text: "Purchase/Builder's contract price\n",
                style: 'label'
              },
              {
                text: (fundRequired?.builderContractPrice || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Land contract/Land value\n',
                style: 'label'
              },
              {
                text: (fundRequired?.landValue || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Existing loan balance\n',
                style: 'label'
              },
              {
                text: (fundRequired?.existingLoanBalance || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Exit fee\n',
                style: 'label'
              },
              {
                text: (fundRequired?.exitFee || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          }
        ],
        [
          {
            text: [
              {
                text: 'Stamp duty\n',
                style: 'label'
              },
              {
                text: (fundRequired?.stampDuty || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Land transfer fees\n',
                style: 'label'
              },
              {
                text: (fundRequired?.landTransferFees || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Mortgage registration fees\n',
                style: 'label'
              },
              {
                text: (fundRequired?.mortgageRegistrationFees || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Mortgage discharge costs\n',
                style: 'label'
              },
              {
                text: (fundRequired?.mortgageDischargeCosts || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          }
        ],
        [
          {
            text: [
              {
                text: 'Lender fees\n',
                style: 'label'
              },
              {
                text: (fundRequired?.lenderFees || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Legal fees\n',
                style: 'label'
              },
              {
                text: (fundRequired?.legalFees || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Other fees/costs\n',
                style: 'label'
              },
              {
                text: (fundRequired?.mortgageRegistrationFees || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Other debt\n',
                style: 'label'
              },
              {
                text: _.reduce(
                  fundRequired?.otherDebts || [],
                  (sum, n) => _.add(sum, helperService.isNumber(n?.value, true)),
                  0
                )?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          }
        ],
        [
          {
            text: [
              {
                text: 'Proposed loan amount\n',
                style: 'label'
              },
              {
                text: (fundAvailable?.proposedLoanAmount || 0)?.formatWithCurrency(
                  currentCurrency,
                  0
                ),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Savings\n',
                style: 'label'
              },
              {
                text: (fundAvailable?.savings || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'Deposit paid\n',
                style: 'label'
              },
              {
                text: (fundAvailable?.depositPaid || 0)?.formatWithCurrency(currentCurrency, 0),
                style: 'value'
              }
            ]
          },
          {
            text: [
              {
                text: 'LVR\n',
                style: 'label'
              },
              {
                text: `${helperService.round({
                  value: fundAvailable?.baseLvr
                    ? _.divide(_.multiply(fundAvailable.baseLvr, 100), 100)
                    : 0
                })}%`,
                style: 'value'
              }
            ]
          }
        ]
      );
    }
    return data;
  };

  /**
   * Get total of funds required.
   *
   * @param {Array} homeLoanSecurities
   * @param {Object} fundingWorksheets
   *
   * @returns {Object}
   */
  const getTotalsOfFundsRequired = ({ homeLoanSecurities, fundingWorksheets }) => {
    let totalPurposeFunds = 0;
    let totalExistingLoanBalance = 0;
    let totalExitFees = 0;
    let totalStampDuty = 0;
    let totalLandTransferFees = 0;
    let totalMortgageRegistrationFees = 0;
    let totalLenderFees = 0;
    let totalLegalFees = 0;
    let totalMortgageDischargeCosts = 0;
    let totalOtherFees = 0;
    let totalOtherDebts = 0;
    const fundsRequiredData = [];

    for (let i = 0; i < fundingWorksheets?.fundsRequired.length; i += 1) {
      const fundRequired = fundingWorksheets.fundsRequired[i];
      const fA = fundingWorksheets?.fundsAvailable?.length > 1 ? i : 0;
      const fundAvailable = fundingWorksheets?.fundsAvailable[fA];
      totalPurposeFunds += helperService.isNumber(fundRequired?.purposeFunds || 0, true);
      totalExistingLoanBalance += helperService.isNumber(
        fundRequired?.existingLoanBalance || 0,
        true
      );
      totalExitFees += helperService.isNumber(fundRequired?.exitFee || 0, true);
      totalStampDuty += helperService.isNumber(fundRequired?.stampDuty || 0, true);
      totalLandTransferFees += helperService.isNumber(
        fundRequired?.landTransferFees || 0,
        true
      );
      totalMortgageRegistrationFees += helperService.isNumber(
        fundRequired?.mortgageRegistrationFees || 0,
        true
      );
      totalLenderFees += helperService.isNumber(fundRequired?.lenderFees || 0, true);
      totalLegalFees += helperService.isNumber(fundRequired?.legalFees || 0, true);
      totalMortgageDischargeCosts += helperService.isNumber(
        fundRequired?.mortgageDischargeCosts || 0,
        true
      );
      totalOtherFees += helperService.isNumber(fundRequired?.otherFees || 0, true);
      totalOtherDebts += _.reduce(
        fundRequired?.otherDebts || [],
        (sum, n) => _.add(sum, helperService.isNumber(n?.value, true)),
        0
      );
      const data = prepareFundsDetailsBySecurityForAppForm({
        homeLoanSecurities,
        fundRequired,
        fundAvailable
      });
      if (data?.length) {
        fundsRequiredData.push(data);
      }
    }
    return {
      totalPurposeFunds,
      totalExistingLoanBalance,
      totalExitFees,
      totalStampDuty,
      totalLandTransferFees,
      totalMortgageRegistrationFees,
      totalLenderFees,
      totalLegalFees,
      totalMortgageDischargeCosts,
      totalOtherFees,
      totalOtherDebts,
      fundsRequiredData
    };
  };

  /**
   * Get total of funds available.
   *
   * @param {Array} fundsAvailable
   *
   * @returns {Object}
   */
  const getTotalsOfFundsAvailable = ({ fundsAvailable }) => {
    let totalLoanAmount = 0;
    let totalFhog = 0;
    let totalSaleProceedFunds = 0;
    let totalSavings = 0;
    let totalDepositPaid = 0;
    let totalEquityFromProperty = 0;
    let totalGift = 0;
    let totalOtherFundsAvailable = 0;
    let totalLmi = 0;
    let totalLoanAmountWithLmi = 0;

    for (let i = 0; i < fundsAvailable.length; i += 1) {
      const fundAvailable = fundsAvailable[i];
      totalLoanAmount += helperService.isNumber(fundAvailable.proposedLoanAmount, true);
      totalFhog += helperService.isNumber(fundAvailable.firstHomeOwnersGrant, true);
      totalSaleProceedFunds += helperService.isNumber(fundAvailable.saleProceedFunds, true);
      totalSavings += helperService.isNumber(fundAvailable.savings, true);
      totalDepositPaid += helperService.isNumber(fundAvailable.depositPaid, true);
      totalEquityFromProperty += helperService.isNumber(fundAvailable.equityFromProperty, true);
      totalGift += helperService.isNumber(fundAvailable.gift, true);
      totalOtherFundsAvailable += helperService.isNumber(
        fundAvailable.otherFundsAvailable,
        true
      );
      totalLmi += helperService.isNumber(fundAvailable.lmi, true);
      totalLoanAmountWithLmi += fundAvailable.isCapitalize
        ? helperService.isNumber(fundAvailable.proposedLoanAmount, true) + totalLmi
        : helperService.isNumber(fundAvailable.proposedLoanAmount, true);
    }
    return {
      totalLoanAmount,
      totalFhog,
      totalSaleProceedFunds,
      totalSavings,
      totalDepositPaid,
      totalEquityFromProperty,
      totalGift,
      totalOtherFundsAvailable,
      totalLmi,
      totalLoanAmountWithLmi
    };
  };

  return {
    viewOrSavePdf,
    createHeader,
    createSubHeader,
    prepareFundsDetailsBySecurityForAppForm,
    getTotalsOfFundsRequired,
    getTotalsOfFundsAvailable
  };
};
